import axiosInstance, {endpoints} from 'src/lib/axios';

import {toast} from 'src/components/snackbar';

import type {Bankkontotyp, BanktransaktionerParsningsRespons, IBankkonto, IBanktransaktion} from "../../types/nya/ekonomi/bank/BankTyper";


export function parsaBanktransaktioner(file: File, orgId: string, kontoId: string): Promise<BanktransaktionerParsningsRespons> {
    const formData = new FormData();
    formData.append('banktransaktionsfil', file);

    const url = `${endpoints.bank.banktransaktioner.filuppladdning(orgId)}?kontoId=${kontoId}`;


    return axiosInstance.post(url, formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    })
        .then(response => response.data)
        .catch(error => {
            console.error('Fel vid upload av banktransaktioner:', error);
            throw error;
        });
}

export function skapaBankkonto(
    orgId: string | undefined,
    namn: string,
    bankkontotyp?: Bankkontotyp,
    bokföringskontonummer?: number
): Promise<import("../../types/nya/ekonomi/bank/BankTyper").IBankkonto> {
    if (!orgId) {
        return Promise.reject(new Error("Ingen organisation vald"));
    }
    const params: Record<string, string | number> = {namn};
    if (bankkontotyp) params.bankkontotyp = bankkontotyp;
    if (bokföringskontonummer != null) params.bokforingskontonummer = bokföringskontonummer;
    return axiosInstance.post(endpoints.bank.konton.skapa(orgId), null, {params})
        .then(response => response.data)
        .catch(error => {
            toast.error(error.response?.data?.message || error.message);
            throw error;
        });
}

export function laddaUppKontoutdrag(
    bankkontoId: string,
    fil: File
): Promise<import("../../types/nya/ekonomi/bank/BankTyper").IBankkonto> {
    const formData = new FormData();
    formData.append('kontoutdrag', fil);
    return axiosInstance.post(endpoints.bank.konton.kontoutdrag(bankkontoId), formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    })
        .then(response => response.data)
        .catch(error => {
            toast.error(error.message);
            throw error;
        });
}

export function uppdateraBokföringskontonummer(
    bankkontoId: string,
    bokföringskontonummer: number
): Promise<import("../../types/nya/ekonomi/bank/BankTyper").IBankkonto> {
    return axiosInstance
        .put(endpoints.bank.konton.bokföringskontonummer(bankkontoId), null, {
            params: {'bokforingskontonummer': bokföringskontonummer},
        })
        .then(response => response.data);
}

export function hämtaDubbletter(
    orgId: string,
    query: { iban?: string; kontonummer?: string; namn?: string }
): Promise<import("../../types/nya/ekonomi/bank/BankTyper").IBankkonto[]> {
    return axiosInstance
        .get(endpoints.bank.konton.dubbletter(orgId), { params: query })
        .then(response => response.data)
        .catch(error => {
            return [];
        });
}

export function hämtaInternaTransaktionerAttBokföra(
    orgId: string
): Promise<import("../../types/nya/ekonomi/bank/BankTyper").IInternaTransaktionerResponse> {
    return axiosInstance
        .get(endpoints.bank.interna.hämta(orgId))
        .then(response => response.data);
}

export type SparkontoBokföringstyp =
    | 'RÄNTA'
    | 'ÖVERFÖRING_TILL_TRANSAKTIONSKONTO'
    | 'ÖVERFÖRING_FRÅN_TRANSAKTIONSKONTO';

export function bokförSparkontoTransaktion(
    orgId: string,
    banktransaktionId: string,
    typ: SparkontoBokföringstyp
): Promise<import("../../types/nya/ekonomi/bokforing/bokforingstyper").IAffärshändelse> {
    return axiosInstance
        .post(endpoints.bank.bokföringSparkonton.bokför(orgId), {banktransaktionId, typ})
        .then(response => response.data)
        .catch(error => {
            const message =
                error?.response?.data?.message
                || error?.message
                || 'Kunde inte bokföra sparkontotransaktion';
            const wrapped: any = new Error(message);
            wrapped.status = error?.response?.status;
            throw wrapped;
        });
}

export function bokförInternaTransaktioner(
    orgId: string
): Promise<import("../../types/nya/ekonomi/bokforing/bokforingstyper").IAffärshändelse[]> {
    return axiosInstance
        .post(endpoints.bank.interna.bokför(orgId))
        .then(response => response.data)
        .catch(error => {
            // Backend kastar SaknarBokföringskontonummerException (409) om minst ett bankkonto
            // saknar bokföringskontonummer — bubbla upp med meddelandet så att handlern på
            // ovan nivå kan visa namnen för användaren.
            const message =
                error?.response?.data?.message
                || error?.message
                || 'Kunde inte bokföra interna utkast';
            const wrapped: any = new Error(message);
            wrapped.status = error?.response?.status;
            wrapped.details = error?.response?.data?.errors;
            throw wrapped;
        });
}

export function resetInternaTransaktioner(orgId: string): Promise<void> {
    return axiosInstance
        .post(endpoints.bank.interna.reset(orgId))
        .then(() => {
            toast.success("State återställd för interna transaktioner");
        })
        .catch(error => {
            toast.error(error.message);
        });
}

export function flyttaBanktransaktionTillBankkonto(
    orgId: string,
    banktransaktionId: string,
    nyttBankkontoId: string
): Promise<void> {
    return axiosInstance
        .post(endpoints.bank.banktransaktioner.flyttaBankkonto(orgId, banktransaktionId), null, {
            params: {nyttBankkontoId},
        })
        .then(() => undefined)
        .catch(error => {
            const message = error?.response?.data?.message || error.message;
            toast.error(message);
            throw error;
        });
}

export function hämtaBankkonton(orgId: string): Promise<import("../../types/nya/ekonomi/bank/BankTyper").IBankkonto[]> {
    return axiosInstance.get(endpoints.bank.konton.hämta(orgId))
        .then(response => response.data)
        .catch(error => {
            toast.error(error.message);
            return [];
        });
}

export function uppdateraBankkontoNamn(id: string, namn: string): Promise<void> {
    return axiosInstance.put(endpoints.bank.konton.namn(id), null, {params: {namn}})
        .then(() => {
            toast.success("Namn uppdaterat");
        })
        .catch(error => {
            toast.error(error.message);
        });
}

export function hämtaSaldoHistorik(id: string, manader: number): Promise<import("../../types/nya/ekonomi/bank/BankTyper").IBankkontosaldo[]> {
    return axiosInstance.get(endpoints.bank.konton.saldoHistorik(id, manader))
        .then(response => response.data)
        .catch(error => {
            toast.error(error.message);
            return [];
        });
}

export function hämtaNyaBanktransaktioner(orgId: string): Promise<void> {
    return axiosInstance.post(endpoints.bank.banktransaktioner.hämtaNya(orgId))
        .then(() => {
        })
        .catch(error => {
            toast.error(error.message);
        });
}

export function raderaBankkonto(bankkontoId: string): Promise<void> {
    return axiosInstance.delete(endpoints.bank.konton.radera(bankkontoId))
        .then(() => {
            toast.success('Bankkonto raderat');
        })
        .catch(error => {
            toast.error(error?.response?.data?.message || error.message);
            throw error;
        });
}

export function slåIhopBankkonton(
    källaId: string,
    målId: string
): Promise<import("../../types/nya/ekonomi/bank/BankTyper").IBankkonto> {
    return axiosInstance.post(endpoints.bank.konton.slåIhop(källaId), null, {
        params: {malId: målId},
    })
        .then(response => {
            toast.success('Konton ihopslagna');
            return response.data;
        })
        .catch(error => {
            toast.error(error?.response?.data?.message || error.message);
            throw error;
        });
}

/**
 * Skapar platshållarkonton för alla kontoId:n i banktransaktioner som saknar
 * ett matchande bankkonto. Returnerar de nyskapade kontona (tom lista om inga saknades).
 * Idempotent — anropa gärna vid varje sidladdning.
 */
export function lösOrphaneadeKonton(
    orgId: string
): Promise<import("../../types/nya/ekonomi/bank/BankTyper").IBankkonto[]> {
    return axiosInstance
        .post(endpoints.bank.konton.lösOrphaner(orgId))
        .then(response => response.data)
        .catch(error => {
            console.error('[BankService] lösOrphaneadeKonton fel:', error);
            return [];
        });
}

export function hämtaDubblettkandidater(
    orgId: string
): Promise<import("../../types/nya/ekonomi/bank/BankTyper").IDubblettkandidat[]> {
    return axiosInstance
        .get(endpoints.bank.konton.dubblettkandidater(orgId))
        .then(response => response.data)
        .catch(error => {
            console.error('[BankService] hämtaDubblettkandidater fel:', error);
            return [];
        });
}

export function ignoreraDubblettkandidat(
    orgId: string,
    kontoIdA: string,
    kontoIdB: string,
): Promise<void> {
    return axiosInstance
        .post(endpoints.bank.konton.ignoreraDubblettkandidat(orgId), null, {
            params: { kontoIdA, kontoIdB },
        })
        .then(() => undefined)
        .catch(error => {
            console.error('[BankService] ignoreraDubblettkandidat fel:', error);
            toast.error(error?.response?.data?.message || error.message);
            throw error;
        });
}

export function hämtaSaldon(orgId: string, visaToast: boolean = true): Promise<void> {
    return axiosInstance.post(endpoints.bank.konton.saldon(orgId))
        .then(() => {
            if (visaToast) {
                toast.success("Saldon uppdaterade");
            }
        })
        .catch(error => {
            if (visaToast) {
                toast.error(error.message);
            }
        });
}

export function knytOmappadeTransaktionerTillKonto(orgId: string, kontoId: string): Promise<void> {
    return axiosInstance.post(`/api/bank/v1/konton/${kontoId}/knyt-omappade`, null, {
        params: { orgId }
    }).then(() => {
        toast.success("Omappade transaktioner knöts till kontot");
    }).catch(error => {
        console.error(error);
        toast.error("Kunde inte knyta omappade transaktioner");
        throw error;
    });
}

export function markeraBanktransaktionSomBokförd(orgId: string, banktransaktionId: string): Promise<void> {
    return axiosInstance
        .post(endpoints.bank.banktransaktioner.markeraBokförd(orgId, banktransaktionId))
        .then(() => undefined)
        .catch(error => {
            const message = error?.response?.data?.message || error.message;
            toast.error(message);
            throw error;
        });
}

export function hämtaTransaktionerFörBokföringskontonummer(
    orgId: string,
    bokföringskontonummer: number
): Promise<{ transaktioner: import("../../types/nya/ekonomi/bank/BankTyper").IBanktransaktion[] }> {
    return axiosInstance
        .get(endpoints.bank.banktransaktioner.förBokföringskontonummer(orgId, bokföringskontonummer))
        .then(response => response.data)
        .catch(error => {
            console.error('[BankService] hämtaTransaktionerFörBokföringskontonummer fel:', error);
            return { transaktioner: [] };
        });
}

export function kopplaBanktransaktionTillAffärshändelse(
    orgId: string,
    banktransaktionId: string,
    affärshändelseId: string
): Promise<void> {
    return axiosInstance
        .post(endpoints.bank.banktransaktioner.kopplaTillAffärshändelse(orgId), {
            banktransaktionId,
            affärshändelseId,
        })
        .then(() => {
            toast.success('Banktransaktionen kopplades till affärshändelsen');
        })
        .catch(error => {
            const message = error?.response?.data?.message || error.message;
            toast.error(message);
            throw error;
        });
}

export function kopplaAffärshändelserTillBanktransaktioner(orgId: string): Promise<{ antalKopplade: number }> {
    return axiosInstance.post(`/api/bank/v1/banktransaktioner/${orgId}/koppla-affarshandelser`)
        .then(response => {
            const antal = response.data.antalKopplade;
            if (antal > 0) {
                toast.success(`${antal} banktransaktioner kopplades automatiskt till affärshändelser`);
            } else {
                toast.info('Inga nya banktransaktioner kunde kopplas automatiskt');
            }
            return response.data;
        })
        .catch(error => {
            console.error(error);
            toast.error("Ett fel uppstod vid automatisk koppling");
            throw error;
        });
}

/**
 * Sorterar bankkonton enligt Flinkyens regler:
 * - Oanvända konton med 0 i saldo ska alltid ligga längst ner i kontolistan (räknas ut i frontend).
 * - Ett konto räknas som oanvänt med 0 i saldo om saldo === 0 och det saknar transaktioner (eller har "oanv" i namnet).
 * - Konton med saldo !== 0 visas överst, sorterade fallande på saldo.
 * - Konton med saldo === 0 men som har tidigare transaktioner visas före helt oanvända konton.
 */
export function sorteraBankkonton(
    konton: IBankkonto[],
    transaktioner: IBanktransaktion[] = []
): IBankkonto[] {
    const transaktionsKarta = new Map<string, number>();
    for (const t of transaktioner) {
        if (t.kontoId) {
            transaktionsKarta.set(t.kontoId, (transaktionsKarta.get(t.kontoId) || 0) + 1);
        }
    }

    const ärOanväntNollsaldo = (k: IBankkonto) => {
        const saldo = k.saldo ?? 0;
        if (saldo !== 0) return false;
        const antalTransaktioner = transaktionsKarta.get(k.id) ?? 0;
        const namnSägerOanvänt = /oanv/i.test(k.namn || '');
        return antalTransaktioner === 0 || namnSägerOanvänt;
    };

    return [...konton].sort((a, b) => {
        const aOanvänt = ärOanväntNollsaldo(a);
        const bOanvänt = ärOanväntNollsaldo(b);

        // Oanvända konton med 0 i saldo ska alltid ligga längst ner
        if (aOanvänt && !bOanvänt) return 1;
        if (!aOanvänt && bOanvänt) return -1;

        const saldoA = a.saldo ?? 0;
        const saldoB = b.saldo ?? 0;

        // Konton med saldo !== 0 före konton med saldo === 0
        if (saldoA !== 0 && saldoB === 0) return -1;
        if (saldoA === 0 && saldoB !== 0) return 1;

        // Fallande på saldo (störst saldo först)
        if (saldoB !== saldoA) return saldoB - saldoA;

        // Namnsortering som fallback
        return (a.namn || '').localeCompare(b.namn || '', 'sv');
    });
}

